import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  useCreateAnnotationMutation,
  useDeleteAnnotationMutation,
  useDismissStalenessMutation,
  useGetRecentAnnotationsQuery,
  useGetThreadQuery,
  useReviewFixtureMutation,
  useGetFixtureReviewsQuery,
  useToggleResolveMutation,
  useUpdateAnnotationMutation,
} from '@verantos-dev/data-access-cohorts';

import type { FixtureReview } from './types';

export function useAnnotationThread(targetId: string, skip = false) {
  const { data, isLoading, refetch } = useGetThreadQuery(targetId, { skip });
  const [createAnnotation] = useCreateAnnotationMutation();
  const [updateAnnotation] = useUpdateAnnotationMutation();
  const [deleteAnnotation] = useDeleteAnnotationMutation();
  const [toggleResolve] = useToggleResolveMutation();
  const [dismissStaleness] = useDismissStalenessMutation();

  const addComment = useCallback(
    async (text: string, contentHash: string) => {
      await createAnnotation({ targetId, text, contentHash });
    },
    [createAnnotation, targetId]
  );

  const editComment = useCallback(
    async (annotationId: string, text: string) => {
      await updateAnnotation({ targetId, annotationId, text });
    },
    [updateAnnotation, targetId]
  );

  const removeComment = useCallback(
    async (annotationId: string) => {
      await deleteAnnotation({ targetId, annotationId });
    },
    [deleteAnnotation, targetId]
  );

  const setResolved = useCallback(
    async (resolved: boolean) => {
      await toggleResolve({ targetId, resolved });
    },
    [toggleResolve, targetId]
  );

  const dismissStale = useCallback(async () => {
    await dismissStaleness(targetId);
  }, [dismissStaleness, targetId]);

  return {
    thread: data,
    isLoading,
    refetch,
    addComment,
    editComment,
    removeComment,
    setResolved,
    dismissStale,
  };
}

export function useAnnotationPolling() {
  const sinceRef = useRef(new Date(Date.now() - 60000).toISOString());

  const { data } = useGetRecentAnnotationsQuery(sinceRef.current, {
    pollingInterval: 30000,
    skipPollingIfUnfocused: true,
  });

  useEffect(() => {
    if (data) {
      sinceRef.current = new Date().toISOString();
    }
  }, [data]);

  return data;
}

export function useFixtureReviews(): {
  reviews: Record<string, FixtureReview>;
  toggleReview: (fixtureId: string) => void;
} {
  const { data } = useGetFixtureReviewsQuery();
  const [reviewFixture] = useReviewFixtureMutation();

  const reviews = useMemo(() => {
    const map: Record<string, FixtureReview> = {};
    if (data) {
      for (const r of data) {
        map[r.fixtureId] = r;
      }
    }
    return map;
  }, [data]);

  const toggleReview = useCallback(
    (fixtureId: string) => {
      const isCurrentlyReviewed = !!reviews[fixtureId];
      reviewFixture({ fixtureId, reviewed: !isCurrentlyReviewed });
    },
    [reviews, reviewFixture]
  );

  return { reviews, toggleReview };
}
